<template>
	<div class="navigator-header p-mb-3">
		<p class="title p-my-2">{{$t("Squeeze.Reports.Processed")}}</p>
	</div>
	<div class="p-fluid p-component p-formgrid p-grid p-input-filled">
		<div class="p-field p-col-6 p-sm-4">
			<Dropdown
				v-model="selectedYear"
				:options="years"
				optionLabel="name"
				:filter="true"
				:autoFilterFocus="true"
				@change="onChangeYears($event)"
				@show="showDropdownOverlay"
			>
				<template #value="slotProps">
					<div class="country-item country-item-value" v-if="slotProps.value">
						<div>{{ slotProps.value.name }}</div>
					</div>
					<span v-else>
                    {{ slotProps.placeholder }}
                </span>
				</template>
				<template #option="slotProps">
					<div class="country-item">
						<div>{{ slotProps.option.name }}</div>
					</div>
				</template>
			</Dropdown>
		</div>
		<div class="p-field p-col-6 p-sm-4">
			<Dropdown
				v-model="selectedMonth"
				:options="months"
				:placeholder="$t('Squeeze.Reports.PlaceholderMonth')"
				optionLabel="name"
				:showClear="true"
				:filter="true"
				:autoFilterFocus="true"
				@change="onChangeMonths($event)"
				@show="showDropdownOverlay"
			>
				<template #value="slotProps">
					<div class="month-item month-item-value" v-if="slotProps.value">
						<div>{{ slotProps.value.name }}</div>
					</div>
					<span v-else>
                    {{ slotProps.placeholder }}
                </span>
				</template>
				<template #option="slotProps">
					<div class="month-item">
						<div>{{ slotProps.option.name }}</div>
					</div>
				</template>
			</Dropdown>
		</div>
		<div class="p-col-12">
			<canvas id="processedReportChart" ref="canvas"></canvas>
		</div>
	</div>
</template>

<script lang="ts">
import {defineComponent, onBeforeMount, ref, watch} from 'vue';
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import {ToastManager} from "@/util/ToastManager";
import { Chart } from 'chart.js';
import moment from "moment";
import Dropdown from "primevue/dropdown";
import {ClientManager} from "@/singletons/ClientManager";
import {showDropdownOverlay} from "@/util/StylesHelper";
import {buildDataSetLabel} from "@/util/ReportHelper";

interface NameValuePair {
	name: string;
	value: number;
}

interface NameValueOptionalPair {
	name: string;
	value?: number;
}

export default defineComponent({
	name: "DocumentReportProcessed",
	methods: {showDropdownOverlay},
	components: {
		Dropdown,
	},
	setup() {
		const {t, locale} = useI18n();
		const toast = useToast();

		/** Current year */
		const currentYear = ref<number>(moment().year());

		/** Current month */
		const currentMonth = ref<number|undefined>(moment().month() +1);

		/** Selected month */
		const selectedMonth = ref<NameValueOptionalPair>({name: ''});

		/** Selected year */
		const selectedYear = ref<NameValuePair>({name: currentYear.value.toString(), value: currentYear.value});

		/** List of all months */
		const months = ref<NameValuePair[]>([]);

		/** Lit of all years */
		const years = ref<NameValuePair[]>([]);

		/** Chart data */
		const chartData = ref<number[]>([]);

		/** Chart labels */
		const chartLabels = ref<string[]>([]);

		/** Label of data set */
		const dataSetLabel = ref<string>('');

		/** Service for queue api */
		const queueAPI = ClientManager.getInstance().squeeze.queue;

		/** Get all Months in current language */
		const getMonths = () => {
			const allMonths = moment.months();
			for (let i = 0; i < allMonths.length; i++) {
				const month = allMonths[i];
				months.value.push({name: month, value: i +1});
			}
		}

		/** Create the chart */
		const createChart = () => {
			const canvas = document.getElementById('processedReportChart') as HTMLCanvasElement;
			const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
			const currentColor = window.getComputedStyle(canvas).getPropertyValue('--dex-secondary-dark-color');
			const totalDocuments = chartData.value.reduce((sum, current) => sum + current, 0);
			new Chart(ctx, {
				type: 'line',
				data: {
					labels: chartLabels.value,
					datasets: [{
						label: `Report ${dataSetLabel.value} (${t('Squeeze.Reports.TotalDocuments')} ${totalDocuments})`,
						data: chartData.value,
						borderWidth: 3,
						borderColor: currentColor,
						backgroundColor: currentColor,
					}],
				},
				options: {
					scales: {
						y: {
							beginAtZero: true,
						},
					},
					plugins: {
						tooltip: {
							callbacks: {
								label: (tooltipItem) => ' ' + tooltipItem.parsed.y,
							},
						},
					},
				},
			});
		}

		/** Get current chart data */
		const getChartData = async () => {
			try {
				let queueReportData = null;
				if (currentMonth.value != undefined) {
					queueReportData = await queueAPI.getQueueReportProcessed(currentYear.value, currentMonth.value);
				} else {
					queueReportData = await queueAPI.getQueueReportProcessed(currentYear.value);
				}

				// Build labels for x axis
				const labels: string[] = [];
				for (const i of queueReportData.labels!) {
					if (queueReportData.labels!.length <= 13) {
						labels.push(moment(i, "MM").format("MMM"));
					} else {
						labels.push(String(i));
					}
				}
				chartLabels.value = labels;

				// Build data
				if (queueReportData.datasets && queueReportData.datasets.length > 0
					&& queueReportData.datasets[0] && queueReportData.datasets[0].data) {
					chartData.value = queueReportData.datasets[0].data;
				} else {
					chartData.value = [];
				}

				// Build Year and Month format
				if (currentMonth.value == null) {
					currentYear.value = parseInt(moment(currentYear.value, "YYYY").format("YYYY"));
				} else {
					currentYear.value = parseInt(moment(currentYear.value && currentMonth.value, "YYYY MM").format("MMMM YY"));
				}

				// Build Chart
				const presentChart = Chart.getChart("processedReportChart");
				if (presentChart) {
					presentChart.destroy();
					createChart();
				} else {
					dataSetLabel.value = buildDataSetLabel(selectedMonth.value?.name, selectedYear.value.name);
					createChart();
				}
			} catch (err: any) {
				ToastManager.showError(toast, t('Squeeze.General.Error'), err);
			}
		}

		/** Triggered when a year is selected to load the report of a year */
		const loadYearReport = () => {
			currentYear.value = selectedYear.value.value;
			currentMonth.value = undefined;
			dataSetLabel.value = selectedYear.value.name;
			getChartData();
		}

		/** Triggered when month is selected to load the report of month */
		const loadMonthReport = () => {
			currentYear.value = selectedYear.value.value;
			currentMonth.value = selectedMonth.value.value;
			dataSetLabel.value = buildDataSetLabel(selectedMonth.value?.name, selectedYear.value.name);
			getChartData();
		}

		/**
		 * Triggered when a month is changed
		 * @param event
		 */
		const onChangeMonths = (event: { originalEvent: PointerEvent; value: { name: string; value: number } }) => {
			selectedMonth.value = event.value;
			if (selectedMonth.value) {
				loadMonthReport();
			} else {
				loadYearReport();
			}
		}

		/**
		 * Triggered when a year is changed
		 * @param event
		 */
		const onChangeYears = (event: { originalEvent: PointerEvent; value: { name: string; value: number } }) => {
			selectedYear.value = event.value;
			currentYear.value = event.value.value;
			dataSetLabel.value = buildDataSetLabel(selectedMonth.value?.name, selectedYear.value.name);
			getChartData();
		}

		onBeforeMount(async () => {
			await getChartData();
			getMonths();

			// set current month
			selectedMonth.value = {name: moment().format("MMMM"), value: currentMonth.value};

			for (let i = 0; i < 31; i++) {
				const year = moment().add(-i, 'years').format('YYYY');
				years.value.push({name: year, value: parseInt(year)});
			}
		})

		/** Watch prop at set value object, because props are not allowed to be mutated */
		watch(locale, () => {
			months.value.splice(0);
			getMonths();

			selectedMonth.value = {name: moment().format("MMMM"), value: currentMonth.value};
			dataSetLabel.value = buildDataSetLabel(selectedMonth.value?.name, selectedYear.value.name);

			const presentChart = Chart.getChart("processedReportChart");
			if (presentChart) {
				presentChart.destroy();
				createChart();
			}
		})

		return {
			t,
			locale,
			toast,
			currentYear,
			currentMonth,
			selectedMonth,
			selectedYear,
			months,
			years,
			chartData,
			chartLabels,
			dataSetLabel,
			getMonths,
			createChart,
			getChartData,
			loadYearReport,
			loadMonthReport,
			onChangeMonths,
			onChangeYears,
		};
	},
});

</script>

<style scoped>

.navigator-header {
	color: var(--dex-primary-color);
}

.title {
	font-size: 2rem;
}

</style>
